import React from "react";

import { refreshTokenAsync, restoreToken } from "../features/auth/authSlice";
import { jwtHelper } from "../helpers/JwtHelper";
import { JWT_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../interceptors/axios";
import { useAppDispatch, useAppSelector } from "./hooks";

/** Token bitiminden bu kadar önce yenilemeye başla (ms). */
const REFRESH_LEAD_MS = 60_000;
/** setTimeout'un güvenle kabul ettiği üst sınır (~24.8 gün). */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Oturumu yönetir:
 *  - Açılışta localStorage'daki token'ı geri yükler; süresi dolmuşsa refresh dener.
 *  - Token bitiminden 1 dk önce tek bir zamanlayıcıyla yeniler.
 *  - Sekme tekrar görünür olduğunda (uyku vb.) süresi dolmuş/dolmak üzere
 *    olan token'ı hemen yeniler.
 */
export default function useAuth() {
  const dispatch = useAppDispatch();
  const { isLoggedIn, exp, loading, tokenRefresing } = useAppSelector(
    (s) => s.auth
  );
  const [initializing, setInitializing] = React.useState(true);

  // Açılış: token'ı geri yükle, gerekiyorsa yenile.
  React.useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const token = localStorage.getItem(JWT_TOKEN_KEY);
      if (token && jwtHelper.isTokenValid(token, () => undefined)) {
        dispatch(restoreToken(token));
      } else if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
        localStorage.removeItem(JWT_TOKEN_KEY);
        await dispatch(refreshTokenAsync());
      } else {
        localStorage.removeItem(JWT_TOKEN_KEY);
      }
      if (!cancelled) setInitializing(false);
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Planlı yenileme: bitiminden REFRESH_LEAD_MS önce.
  React.useEffect(() => {
    if (!isLoggedIn || !exp) return;
    const msLeft = exp * 1000 - Date.now() - REFRESH_LEAD_MS;
    const delay = Math.min(Math.max(msLeft, 0), MAX_TIMEOUT_MS);
    const timer = setTimeout(() => dispatch(refreshTokenAsync()), delay);
    return () => clearTimeout(timer);
  }, [isLoggedIn, exp, dispatch]);

  // Sekme geri geldiğinde (uyku/arka plan) süreyi tekrar kontrol et.
  React.useEffect(() => {
    if (!isLoggedIn || !exp) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (exp * 1000 - Date.now() <= REFRESH_LEAD_MS)
        dispatch(refreshTokenAsync());
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [isLoggedIn, exp, dispatch]);

  return {
    isLoggedIn,
    l: initializing || (tokenRefresing && !isLoggedIn),
    loading,
  };
}
